import { CoordinatorService } from './coordinator.service';
import {
    CSFCUTOFFINFIX,
    CSFCUTOFFSUFIX,
    CSFFUN,
    CSFLISTSEPARATOR,
    CSFPAR,
    CSFPREFIX,
    CSFROLE,
    CSFSUFFIX,
    CSFSUFFIXIF,
    CSPAR,
    CSSTYLE,
} from './template-repository$';
import { XItem } from './x-item';

type CITEPAR = { inDetail?: boolean };

class CitationField implements CSFPAR {
    cutoff: number | undefined;
    cutoffInfix: CSFCUTOFFINFIX | undefined;
    cutoffSufix: CSFCUTOFFSUFIX | undefined;
    fun: CSFFUN | undefined;
    if: string | undefined;
    listSeparator: CSFLISTSEPARATOR | undefined;
    notInDetail: boolean;
    prefix: CSFPREFIX | undefined;
    role: CSFROLE | undefined;
    style: CSSTYLE | undefined;
    suffix: CSFSUFFIX | undefined;
    suffixHide: string | undefined;
    suffixIf: CSFSUFFIXIF | undefined;
    tag: string;

    constructor(
        private parent: Citation,
        params: CSFPAR,
    ) {
        this.cutoff = params.cutoff;
        this.cutoffInfix = params.cutoffInfix;
        this.cutoffSufix = params.cutoffSuffix;
        this.fun = params.fun;
        this.if = params.if;
        this.listSeparator = params.listSeparator;
        this.notInDetail = params.notInDetail || false;
        this.prefix = params.prefix;
        this.style = params.style;
        this.suffix = params.suffix;
        this.suffixHide = params.suffixHide;
        this.suffixIf = params.suffixIf;
        this.tag = params.tag;
    }

    private _applyStyle(txt: string) {
        const styleLs: string[] = [];
        if (this.style?.bold) styleLs.push(`font-weight: bold`);
        if (this.style?.italic) styleLs.push(`font-style: italic`);
        if (this.style?.underline) styleLs.push(`text-decoration: underline`);

        return `<span style="${styleLs.join(';')};">${txt}</span>`;
    }

    private _citeArray(ls: string[]) {
        const suffix = this.suffix || '';
        if (this.cutoff && this.cutoff < ls.length) {
            if (this.cutoffInfix) {
                const last = ls.pop()!;
                ls = ls.slice(0, this.cutoff - 1);
                ls.push(this.cutoffInfix);
                ls.push(last);
            } else {
                ls = ls.slice(0, this.cutoff);
                this.cutoffSufix && ls.push(this.cutoffSufix);
            }
        }

        const txt = ls.join(this.listSeparator);
        return `${txt}${suffix}`;
    }

    private _docxArray(ls: string[]) {
        const suffix = this.suffix || '';
        const txt = ls.join(this.listSeparator);
        return this._docxStyle({ text: this._normalizeSpace(`${txt}${suffix}`) });
    }

    private _docxValue(v: string, xitm: XItem) {
        const prefix = this.prefix || '';
        let suffix = this.suffix || '';
        if (this.suffixHide) {
            const trimmedV = v.trim();
            if (this.suffixHide.includes(trimmedV[trimmedV.length - 1])) suffix = ' ';
        }
        if (this.suffixIf) {
            if (xitm.get(this.suffixIf.if)) suffix = this.suffixIf.char;
        }

        return this._docxStyle({ text: this._normalizeSpace(`${prefix}${v}${suffix}`) });
    }

    private _docxStyle(params: any) {
        if (this.style?.bold) params.bold = true;
        if (this.style?.italic) params.italics = true;
        return params;
    }

    private _citeValue(v: string, xitm: XItem) {
        let txt: string = `${v}`.trim();
        const prefix = this.prefix || '';
        let suffix = this.suffix || '';
        if (this.suffixHide) {
            const lastTextChar = txt.replace(/<[^>]*>/g, '').slice(-1);
            if (this.suffixHide.includes(lastTextChar)) suffix = ' ';
        }
        if (this.suffixIf) {
            if (xitm.get(this.suffixIf.if)) suffix = this.suffixIf.char;
        }

        if (this.fun) {
            txt = this._applyFun(txt);
        } else {
            if (this.style) txt = this._applyStyle(txt);
        }

        return `${prefix}${txt}${suffix}`;
    }

    private _applyFun(txt: string) {
        let label = txt;
        if (this.style) label = this._applyStyle(label);
        switch (this.fun) {
            case 'openDoi':
                const doiHref = `https://dx.doi.org/${txt}`;
                return `<a href="${doiHref}" target="_blank">${label}</a>`;
            case 'openUrl':
                return `<a href="${txt}" target="_blank">${label}</a>`;
        }
        return txt;
    }

    private _isOmit(xitm: XItem) {
        if (this.if) {
            const tag = this.if;
            if (tag.startsWith('!')) return !!xitm.get(tag.slice(1));
            return !xitm.get(tag);
        } else {
            return false;
        }
    }

    docx(finalLs: any[], xitm: XItem, params?: CITEPAR) {
        let v = xitm.get(this.tag);
        if (!v) return;

        if (Array.isArray(v)) return finalLs.push(this._docxArray(v));

        if (v.getFullYear) v = this.parent.coords.utils$.citeDate(v, { noFormat: true });

        return finalLs.push(this._docxValue(v, xitm));
    }

    cite(xitm: XItem, params?: CITEPAR) {
        let v = xitm.get(this.tag);

        if (v) {
            if (v.getFullYear) v = this.parent.coords.utils$.citeDate(v);
            const h$ = this.parent.coords.highlight$;
            if (Array.isArray(v)) {
                v = v.map((v) => h$.run(v, this.tag));
                if (this.tag === 'authorLs') this.parent.coords.utils$.highlightFirstLastAuthor(v);
            } else if (this.tag == 'doi') {
            } else v = h$.run(v, this.tag);
        }
        // const sf = this.parent.coords.search$.getSearchField(this.tag)
        // if (sf) {
        //     v = this.parent.coords.highlight$.run(v, { source: this.tag })
        // }
        if (this.notInDetail && params && params.inDetail) return '';
        if (!v || this._isOmit(xitm)) return '';

        if (Array.isArray(v)) return this._citeArray(v);

        return this._citeValue(v, xitm);
    }

    private _normalizeSpace(txt: string) {
        return txt.replace(/\s+/g, ' ').trim();
    }
}

export class Citation {
    id: string;
    private _fieldMap = new Map<string, CitationField>();

    constructor(
        public coords: CoordinatorService,
        params: CSPAR,
    ) {
        this.id = params.id;
        params.fieldLs.forEach((p) => {
            const f = new CitationField(this, p);
            this._fieldMap.set(f.tag, f);
        });
    }

    cite(xitm: XItem, params?: CITEPAR) {
        const finalLs: string[] = [];
        this._fieldMap.forEach((f) => finalLs.push(f.cite(xitm, params)));
        // const finalLs = this.ruleLs.map(r => r.cite(xitm))
        //     .filter(x => x).join("")
        return finalLs.join('');
    }

    docx(xitm: XItem, params?: CITEPAR) {
        const finalLs: any[] = [];
        this._fieldMap.forEach((f) => f.docx(finalLs, xitm, params));
        return finalLs;
        // const respLs: any[] = []
        // const aLs = (this.get("authorLs") || [])
        //     .map((x: string) => this._normalizeSpace(x))

        // if (aLs.length > 0)
        //     respLs.push({ text: aLs.join("; ") + ": " })

        // respLs.push({ text: this._normalizeSpace(this.get("title")) })

        // const s = this.get("source")
        // if (s)
        //     respLs.push({ text: this._normalizeSpace(s), italics: true })

        // const y = this.get("year")
        // if (y)
        //     respLs.push({ text: this._normalizeSpace(y), bold: true })

        // // { text: aLs.join("; "), bold: true },
        // return respLs
    }
}
